import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { Checkbox } from 'uiComponents/input'
import Money from 'uiComponents/money'
import {
    getSelectedOrderItemsByTableIdAndRowId,
    shouldToggleOrder,
    toggleOrder,
    toggleOrderItem,
} from 'orders/orders/redux'
import ExpandIcon from '../../commons/expandIcon'
import { IOrderItems } from 'orders/schema'
import ItemOptionsSection from './itemOptionsSection'
import ItemDetailsSection from './itemDetailsSection'
import ItemAPFSection from './itemAPFSection'
import './styles.scss'
import ItemCancelationRefundSection from './itemCancelationRefundSection'
import { ReplaceMessagesFunc } from 'messagesContext'
import Infotip from 'uiComponents/infotip'
import { useHasFeature } from 'utils/useHasFeature'

interface OrderItemProps {
    item: IOrderItems
    accountSlug: string
    orderUuid: string
    replaceMessages: ReplaceMessagesFunc
    hideMessage: (id: string) => void
    shouldBeExpanded: boolean
    shouldShowPricesWithoutTax: boolean
}

const OrderItem = ({
    item,
    accountSlug,
    orderUuid,
    replaceMessages,
    hideMessage,
    shouldBeExpanded,
    shouldShowPricesWithoutTax,
}: OrderItemProps) => {
    const [isExpanded, setIsExpanded] = React.useState(shouldBeExpanded ?? true)
    const dispatch = useDispatch()
    const orderShouldToggle = useSelector(shouldToggleOrder(orderUuid, item.id))
    const isItemSelected = useSelector(getSelectedOrderItemsByTableIdAndRowId(orderUuid, item.id))
    const isItemCanceledRefunded = !!item.refundInfo
    const bookingFeesEnabled = useHasFeature({ featureName: 'BookingFees' })

    useEffect(() => {
        if (shouldBeExpanded !== isExpanded) {
            setIsExpanded(shouldBeExpanded)
        }
    }, [shouldBeExpanded])

    const handleCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch(toggleOrderItem({ rowId: item.id, tableId: orderUuid }))
            if (orderShouldToggle) {
                dispatch(toggleOrder({ orderUuid, selected: e.target.checked }))
            }
        },
        [dispatch, item.id, orderShouldToggle, orderUuid],
    )

    return (
        <div className="item">
            <div
                className={classNames('item-row', {
                    selected: isItemSelected,
                    canceledRefunded: isItemCanceledRefunded,
                })}
            >
                <ExpandIcon isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
                <Checkbox
                    id={`order-item-${item.id}-checkbox`}
                    checked={isItemCanceledRefunded ? false : isItemSelected}
                    onChange={handleCheckboxChange}
                    disabled={isItemCanceledRefunded}
                />
                <label htmlFor={`order-item-${item.id}-checkbox`}>
                    <strong>{item.product + ` (${item.productNumericId})`}</strong>
                </label>
                <div
                    className="expand"
                    onClick={() => {
                        setIsExpanded(!isExpanded)
                    }}
                />
                {shouldShowPricesWithoutTax && (
                    <span className="price-excl-tax">
                        Price (excl. tax):&nbsp;
                        <strong>
                            <Money amount={item.discountedPriceExclTax} accountSlug={accountSlug} />
                        </strong>
                    </span>
                )}
                <span className="price-incl-tax">
                    Price:&nbsp;
                    <strong>
                        <Money amount={item.discountedPriceInclTax} accountSlug={accountSlug} />
                    </strong>
                    <Infotip className="tooltip">{`Includes ${
                        bookingFeesEnabled ? 'fees and ' : ''
                    }discounts.`}</Infotip>
                </span>
            </div>
            {isExpanded && (
                <div>
                    <ItemDetailsSection
                        item={item}
                        isItemSelected={isItemSelected}
                        isItemCanceledRefunded={isItemCanceledRefunded}
                        accountSlug={accountSlug}
                        replaceMessages={replaceMessages}
                        hideMessage={hideMessage}
                        shouldShowPricesWithoutTax={shouldShowPricesWithoutTax}
                        shouldShowBookingFees={bookingFeesEnabled}
                    />
                    {item.options.length > 0 && (
                        <ItemOptionsSection
                            options={item.options}
                            isItemSelected={isItemSelected}
                            isItemCanceledRefunded={isItemCanceledRefunded}
                            accountSlug={accountSlug}
                            shouldShowPricesWithoutTax={shouldShowPricesWithoutTax}
                        />
                    )}
                    {!isEmpty(item.afterPaymentForm) && (
                        <ItemAPFSection
                            item={item}
                            isItemSelected={isItemSelected}
                            isItemCanceledRefunded={isItemCanceledRefunded}
                            accountSlug={accountSlug}
                            replaceMessages={replaceMessages}
                            hideMessage={hideMessage}
                        />
                    )}
                    {!!item.refundInfo && (
                        <ItemCancelationRefundSection
                            item={item}
                            accountSlug={accountSlug}
                            isItemSelected={isItemSelected}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default OrderItem
